import React, { Suspense, lazy, useEffect } from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { userRole } from "./helpers.js";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import Protected from "./protectedRoutes.js";
import NavigationDepuisNotification from "./components/notifications/NavigationDepuisNotification.jsx";
import OnboardingObligatoire from "./components/onboarding/OnboardingObligatoire.jsx";
import useSynchroniserLangue from "./hooks/useSynchroniserLangue.js";
import { detecterLangueSysteme } from "./i18n/detecterLangue.js";
import { changeLang } from "./store/lang.js";
import "./httpInterceptor.js";
import "./style/App.css";

// Lazy-loaded route components for high-speed initial page loads
const Index = lazy(() => import("./pages/index.tsx"));
const Login = lazy(() => import("./pages/login/LoginScreen.jsx"));
const SignUp = lazy(() => import("./pages/SignUp.tsx"));
const DashboardAdmin = lazy(() => import("./pages/dashboard/AdminAccueilScreen.jsx"));
const DashboardDistributeur = lazy(() => import("./pages/dashboard/DistAccueilScreen.jsx"));
const Distributors = lazy(() => import("./pages/distributors/AdminDistributeursScreen.jsx"));
const CreateDistributor = lazy(() => import("./pages/distributors/CreateDistributor.tsx"));
const UpdateDistributor = lazy(() => import("./pages/distributors/ProfilDistributeurScreen.jsx"));
const ProfilDistributeur = lazy(() => import("./pages/distributors/ProfilDistributeurScreen.jsx"));
const OrdersListDetails = lazy(() => import("./pages/orders/ImpressionCommandesScreen.jsx"));
const FicheExpedition = lazy(() => import("./pages/orders/FicheExpeditionA4Screen.jsx"));
const FicheExpeditionLot = lazy(() => import("./pages/orders/FicheExpeditionLotScreen.jsx"));
const Developpeur = lazy(() => import("./pages/developpeurs/AdminHomeStockScreen.jsx"));
const CreateDeveloppeur = lazy(() => import("./pages/developpeurs/CreateDeveloppeur.tsx"));
const UpdateDeveloppeur = lazy(() => import("./pages/developpeurs/UpdateDeveloppeur.tsx"));
const DevelopperProfil = lazy(() => import("./pages/developpeurs/DevelopperProfil.tsx"));
const ManageAccount = lazy(() => import("./pages/compte/CompteScreen.jsx"));
const ChangePassword = lazy(() => import("./pages/compte/CompteScreen.jsx"));
const CompteScreen = lazy(() => import("./pages/compte/CompteScreen.jsx"));
const Products = lazy(() => import("./pages/products/AdminProduitsScreen.jsx"));
const ProductDistributors = lazy(() => import("./pages/boutique/BoutiqueScreen.jsx"));
const CartScreen = lazy(() => import("./pages/boutique/CartScreen.jsx"));
const CreateProduct = lazy(() => import("./pages/products/AdminProduitEditionScreen.jsx"));
const EditProduct = lazy(() => import("./pages/products/AdminProduitEditionScreen.jsx"));
const ProductDetails = lazy(() => import("./pages/products/ProductDetails.tsx"));
const Ventes = lazy(() => import("./pages/ventes/AdminVentesScreen.jsx"));
const Orders = lazy(() => import("./pages/orders/AdminCommandesScreen.jsx"));
const AdminCommandeDetail = lazy(() => import("./pages/orders/AdminCommandeDetailScreen.jsx"));
const DistAchats = lazy(() => import("./pages/orders/DistAchatsScreen.jsx"));
const OrderDetails = lazy(() => import("./pages/orders/OrderDetails.tsx"));
const AdminOrderDetails = lazy(() => import("./pages/orders/AdminOrderDetails.tsx"));
const Payouts = lazy(() => import("./pages/payouts/BonusHistoryScreen.jsx"));
const PayoutDetails = lazy(() => import("./pages/payouts/BonusDetailScreen.jsx"));
const BonusDetail = lazy(() => import("./pages/payouts/BonusDetailScreen.jsx"));
const AdminPayouts = lazy(() => import("./pages/payouts/AdminPayoutsScreen.jsx"));
const DistBonus = lazy(() => import("./pages/payouts/DistBonusScreen.jsx"));
const HsBonus = lazy(() => import("./pages/payouts/HsBonusScreen.jsx"));
const HsPortefeuille = lazy(() => import("./pages/wallet/HsPortefeuilleScreen.jsx"));
const BonusFiltres = lazy(() => import("./pages/payouts/BonusFiltersScreen.jsx"));
const Notifications = lazy(() => import("./pages/notifications/NotificationsScreen.jsx"));
const HsOrders = lazy(() => import("./pages/orders/HsOrdersScreen.jsx"));
const HsOrderDetail = lazy(() => import("./pages/orders/HsOrderDetailScreen.jsx"));
const HsExport = lazy(() => import("./pages/orders/HsExportScreen.jsx"));
const Trees = lazy(() => import("./pages/trees/Trees.tsx"));
const HsAccueil = lazy(() => import("./pages/homestock/HsAccueilScreen.jsx"));
const HsCreerDistributeur = lazy(() => import("./pages/homestock/HsCreerDistributeurScreen.jsx"));
const HsDistributeurs = lazy(() => import("./pages/homestock/HsDistributeursScreen.jsx"));
const DistRecompenses = lazy(() => import("./pages/recompenses/DistRecompensesScreen.jsx"));
const AdminRecompenses = lazy(() => import("./pages/recompenses/AdminRecompensesScreen.jsx"));
const RecompenseDetail = lazy(() => import("./pages/recompenses/RecompenseDetailScreen.jsx"));
const AdminNews = lazy(() => import("./pages/news/AdminNewsScreen.jsx"));
const DistNews = lazy(() => import("./pages/news/DistNewsScreen.jsx"));
const AdminNewsEdition = lazy(() => import("./pages/news/AdminNewsEditionScreen.jsx"));
const DidacticielSommaire = lazy(() => import("./pages/didacticiel/DidacticielSommaireScreen.jsx"));
const DidacticielSection = lazy(() => import("./pages/didacticiel/DidacticielSectionScreen.jsx"));
const AdminDidacticiel = lazy(() => import("./pages/didacticiel/AdminDidacticielScreen.jsx"));
const AdminDidacticielEdition = lazy(() => import("./pages/didacticiel/AdminDidacticielEditionScreen.jsx"));

// Initialize i18n once outside the render cycle
i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
  },
  lng: "fr",
  // Français, marché principal de Rise-T : une clé de traduction manquante
  // en anglais doit retomber sur le français, jamais l'inverse.
  fallbackLng: "fr",
  interpolation: {
    escapeValue: false,
  },
});

// Attente du chargement d'un écran — squelette, jamais de roue centrée, comme
// la planche « Composants » l'impose. L'ancienne version utilisait un vert
// #10b981 qui n'appartient à aucune des deux palettes (constat C6).
const PageLoader = () => (
  <div
    style={{ display: "flex", justifyContent: "center", padding: "24px", minHeight: "100vh", background: "var(--bg)" }}
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        maxWidth: "520px",
        height: "fit-content",
        padding: "20px 16px",
        borderRadius: "16px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
      }}
    >
      {[58, 88, 72].map((largeur) => (
        <div
          key={largeur}
          style={{ height: "11px", width: `${largeur}%`, borderRadius: "999px", background: "var(--surface-3)" }}
        />
      ))}
      <span style={{ fontSize: "11.5px", marginTop: "4px", color: "var(--text-3)" }}>Chargement…</span>
    </div>
  </div>
);

function App() {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.user?.value);
  const lang = useSelector((state) => state.lang?.value);
  const user = selector?.payload;

  // La langue choisie est aussi enregistrée côté serveur : les notifications push sont écrites
  // dans la langue du compte (29/09/2026).
  useSynchroniserLangue(lang, user?.token);

  useEffect(() => {
    // Bug corrigé le 14/09/2026 : `lang` se lisait jusqu'ici sur un
    // `.payload` qui n'existait pas (state.lang.value est déjà la valeur,
    // pas une action) — la langue de l'application n'avait donc jamais
    // changé, quel que soit le contenu du store.
    if (!lang) {
      // Premier lancement, aucune langue choisie encore : on détecte celle
      // du système une seule fois et on la mémorise (persistée ensuite comme
      // n'importe quel choix manuel).
      dispatch(changeLang(detecterLangueSysteme(navigator.languages || [navigator.language])));
    } else if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, dispatch]);

  return (
    <div className="App">
      <BrowserRouter>
        <NavigationDepuisNotification />
        <OnboardingObligatoire />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route element={<Protected />}>
              <Route
                path="/dashboard"
                element={
                  <Index subtitle="dashboard" user={user} noShellHeader={true}>
                    {user && user.role === userRole.distributor ? (
                      <DashboardDistributeur />
                    ) : user && user.role === userRole.developper ? (
                      <HsAccueil />
                    ) : (
                      <DashboardAdmin />
                    )}
                  </Index>
                }
              />
              <Route
                path="/distributors"
                element={
                  <Index subtitle="distributors" user={user} noShellHeader={true}>
                    {user && user.role === userRole.developper ? (
                      <HsDistributeurs />
                    ) : (
                      <Distributors />
                    )}
                  </Index>
                }
              />
              <Route
                path="/distributors/profil/:id"
                element={
                  <Index subtitle="Profil distributeur" user={user} noShellHeader={true}>
                    <ProfilDistributeur />
                  </Index>
                }
              />
              <Route
                path="/developpeurs"
                element={
                  <Index subtitle="developpers" user={user} noShellHeader={true}>
                    <Developpeur />
                  </Index>
                }
              />
              <Route
                path="/create-developpeur"
                element={
                  <Index subtitle="createDevelopper" user={user}>
                    <CreateDeveloppeur />
                  </Index>
                }
              />
              <Route
                path="/create-distributor"
                element={
                  <Index
                    subtitle="createDistributor"
                    user={user}
                    /* Le Home Stock a l'écran refondu HsCreerDistributeur, qui porte
                       son appbar ; l'administrateur reste sur l'écran d'origine. */
                    noShellHeader={user?.role === userRole.developper}
                  >
                    {user && user.role === userRole.developper ? (
                      <HsCreerDistributeur />
                    ) : (
                      <CreateDistributor />
                    )}
                  </Index>
                }
              />
              <Route
                path="/developpeur-profil"
                element={
                  <Index subtitle="Profil developper" user={user}>
                    <UpdateDeveloppeur />
                  </Index>
                }
              />
              <Route
                path="/distributor-profil"
                element={
                  <Index subtitle="Profil distributor" user={user} noShellHeader={true}>
                    <ProfilDistributeur />
                  </Index>
                }
              />
              <Route
                path="/distributor-share"
                element={
                  <Index subtitle="Profil distributor" user={user}>
                    <DevelopperProfil />
                  </Index>
                }
              />
              <Route
                path="/developpeur-share"
                element={
                  <Index subtitle="Profil developper" user={user}>
                    <DevelopperProfil />
                  </Index>
                }
              />
              <Route
                path="/manage-account"
                element={
                  <Index subtitle="manageAccount" user={user} noShellHeader={true}>
                    <CompteScreen />
                  </Index>
                }
              />
              <Route
                path="/compte"
                element={
                  <Index subtitle="compte" user={user} noShellHeader={true}>
                    <CompteScreen />
                  </Index>
                }
              />
              <Route
                path="/products"
                element={
                  <Index subtitle="products" user={user} noShellHeader={true}>
                    {user && (user.role === userRole.distributor || user.role === userRole.developper) ? (
                      <ProductDistributors />
                    ) : (
                      <Products />
                    )}
                  </Index>
                }
              />
              <Route
                path="/cart"
                element={
                  <Index subtitle="cart" user={user} noShellHeader={true}>
                    <CartScreen />
                  </Index>
                }
              />
              <Route
                path="/create-product"
                element={
                  <Index subtitle="createProduct" user={user} noShellHeader={true}>
                    <CreateProduct />
                  </Index>
                }
              />
              <Route
                path="/product-details"
                element={
                  <Index subtitle="productDetails" user={user}>
                    <ProductDetails />
                  </Index>
                }
              />
              <Route
                path="/print"
                element={
                  <Index subtitle="orderDetails" user={user}>
                    <OrdersListDetails />
                  </Index>
                }
              />
              <Route
                path="/orders/print"
                element={
                  <Index subtitle="orderDetails" user={user}>
                    <OrdersListDetails />
                  </Index>
                }
              />
              <Route
                path="/orders/slip/:id"
                element={<FicheExpedition />}
              />
              {/* Maquette ImpressionCommandes : le bordereau de LOT du Home Stock,
                  qui couvre toutes les commandes en attente. Le bordereau d une
                  seule commande reste sur /orders/slip/:id. */}
              <Route
                path="/fiche-expedition"
                element={<FicheExpeditionLot />}
              />
              <Route
                path="/edit-product"
                element={
                  <Index subtitle="editProduct" user={user} noShellHeader={true}>
                    <EditProduct />
                  </Index>
                }
              />
              <Route
                path="/change-password"
                element={
                  <Index subtitle="changePassword" user={user} noShellHeader={true}>
                    <CompteScreen />
                  </Index>
                }
              />
              <Route
                path="/payouts"
                element={
                  <Index subtitle="payouts" user={user} noShellHeader={true}>
                    {/* Trois écrans distincts, comme le canevas les décrit :
                        l'administrateur règle des bénéficiaires (AdminPayouts),
                        le distributeur suit ses gains (DistBonus), le Home Stock
                        sa commission (HsBonus). L'historique détaillé, commun aux
                        trois, vit sur /payouts/historique. */}
                    {user && user.role === userRole.admin ? (
                      <AdminPayouts />
                    ) : user && user.role === userRole.developper ? (
                      <HsBonus />
                    ) : (
                      <DistBonus />
                    )}
                  </Index>
                }
              />
              <Route
                path="/wallet"
                element={
                  <Index subtitle="wallet" user={user} noShellHeader={true}>
                    <HsPortefeuille />
                  </Index>
                }
              />
              <Route
                path="/payouts/historique"
                element={
                  <Index subtitle="payouts" user={user} noShellHeader={true}>
                    <Payouts />
                  </Index>
                }
              />
              <Route
                path="/payouts/filtres"
                element={
                  <Index subtitle="payouts" user={user} noShellHeader={true}>
                    <BonusFiltres />
                  </Index>
                }
              />
              <Route
                path="/payouts/:id"
                element={
                  <Index subtitle="payouts" user={user} noShellHeader={true}>
                    <BonusDetail />
                  </Index>
                }
              />
              <Route
                path="/payout-details"
                element={
                  <Index subtitle="payouts" user={user} noShellHeader={true}>
                    <PayoutDetails />
                  </Index>
                }
              />
              <Route
                path="/notifications"
                element={
                  <Index subtitle="notifications" user={user} noShellHeader={true}>
                    <Notifications />
                  </Index>
                }
              />
              <Route
                path="/ventes"
                element={
                  /* A20 — /ventes est l'écran d'analyse des ventes de
                     l'administrateur, et lui seul. Les deux écrans que le Home
                     Stock et le distributeur y trouvaient faisaient double
                     emploi avec HsCommandes et DistAchats, déjà refondus, et
                     aucune navigation ne les atteignait. Ils sont redirigés
                     vers /orders, qui sert déjà ces deux écrans. */
                  user && user.role === userRole.developper ? (
                    <Navigate to="/orders" replace />
                  ) : user && user.role === userRole.distributor ? (
                    <Navigate to="/orders" replace />
                  ) : (
                    <Index subtitle="sell" user={user} noShellHeader={true}>
                      <Ventes />
                    </Index>
                  )
                }
              />
              <Route
                path="/orders"
                element={
                  <Index subtitle="orders" user={user} noShellHeader={true}>
                    {user && user.role === userRole.developper ? (
                      <HsOrders />
                    ) : user && user.role === userRole.distributor ? (
                      <DistAchats />
                    ) : (
                      <Orders />
                    )}
                  </Index>
                }
              />
              <Route
                path="/achats"
                element={
                  <Index subtitle="Mes achats" user={user} noShellHeader={true}>
                    <DistAchats />
                  </Index>
                }
              />
              <Route
                path="/orders/export"
                element={
                  <Index subtitle="orders" user={user} noShellHeader={true}>
                    <HsExport />
                  </Index>
                }
              />
              <Route
                path="/orders/:id"
                element={
                  /* M11 — l'administrateur a sa propre maquette de détail :
                     il n'y voit aucun effet sur un portefeuille, puisque le
                     sien n'est jamais débité, mais il y voit quelle agence doit
                     servir la commande. */
                  <Index subtitle="orders" user={user} noShellHeader={true}>
                    {user && user.role === userRole.admin ? (
                      <AdminCommandeDetail />
                    ) : (
                      <HsOrderDetail />
                    )}
                  </Index>
                }
              />
              <Route
                path="/trees"
                element={
                  <Index subtitle="trees" user={user} noShellHeader={true}>
                    <Trees />
                  </Index>
                }
              />
              <Route
                path="/order-details"
                element={
                  <Index subtitle="orderDetails" user={user}>
                    <OrderDetails />
                  </Index>
                }
              />
              {/* Récompenses en nature — constat F12.
                  /recompenses sert l'écran du rôle : la file de remise pour
                  l'administrateur, la progression pour le distributeur.
                  /recompenses/me force la vue du distributeur, et
                  /recompenses/:id le détail d'un franchissement, lisible par
                  les deux — un distributeur ne voit que les siens. */}
              <Route
                path="/recompenses"
                element={
                  <Index subtitle="recompenses" user={user} noShellHeader={true}>
                    {user && user.role === userRole.admin ? (
                      <AdminRecompenses />
                    ) : (
                      <DistRecompenses />
                    )}
                  </Index>
                }
              />
              <Route
                path="/recompenses/me"
                element={
                  <Index subtitle="recompenses" user={user} noShellHeader={true}>
                    <DistRecompenses />
                  </Index>
                }
              />
              <Route
                path="/recompenses/:id"
                element={
                  <Index subtitle="recompenses" user={user} noShellHeader={true}>
                    <RecompenseDetail />
                  </Index>
                }
              />
              {/* Actualités (module News) — reçu le 10/09/2026, maquettes
                  validées le 12/09/2026. /news sert l'écran du rôle : la liste
                  d'administration pour l'Admin, le flux de lecture pour les
                  autres rôles. /news/creer et /news/modifier sont réservées à
                  l'administrateur (le contrôleur les refuse aux autres). */}
              <Route
                path="/news"
                element={
                  <Index subtitle="news" user={user} noShellHeader={true}>
                    {user && user.role === userRole.admin ? <AdminNews /> : <DistNews />}
                  </Index>
                }
              />
              <Route
                path="/news/creer"
                element={
                  <Index subtitle="news" user={user} noShellHeader={true}>
                    <AdminNewsEdition />
                  </Index>
                }
              />
              <Route
                path="/news/modifier"
                element={
                  <Index subtitle="news" user={user} noShellHeader={true}>
                    <AdminNewsEdition />
                  </Index>
                }
              />
              {/* Didacticiel — reçu et précisé le 12/09/2026, maquettes
                  validées le même jour. /didacticiel et /didacticiel/:id
                  servent la lecture, pour les trois rôles (le serveur ne
                  renvoie jamais que le contenu du rôle du demandeur).
                  /didacticiel/gestion* est réservé à l'administrateur (le
                  contrôleur le refuse aux autres, comme /news/creer). */}
              <Route
                path="/didacticiel"
                element={
                  <Index subtitle="didacticiel" user={user} noShellHeader={true}>
                    <DidacticielSommaire />
                  </Index>
                }
              />
              <Route
                path="/didacticiel/gestion"
                element={
                  <Index subtitle="didacticiel" user={user} noShellHeader={true}>
                    <AdminDidacticiel />
                  </Index>
                }
              />
              <Route
                path="/didacticiel/gestion/creer"
                element={
                  <Index subtitle="didacticiel" user={user} noShellHeader={true}>
                    <AdminDidacticielEdition />
                  </Index>
                }
              />
              <Route
                path="/didacticiel/gestion/modifier/:id"
                element={
                  <Index subtitle="didacticiel" user={user} noShellHeader={true}>
                    <AdminDidacticielEdition />
                  </Index>
                }
              />
              <Route
                path="/didacticiel/:id"
                element={
                  <Index subtitle="didacticiel" user={user} noShellHeader={true}>
                    <DidacticielSection />
                  </Index>
                }
              />
              <Route
                path="/admin-order-details"
                element={
                  <Index subtitle="orderDetails" user={user}>
                    <AdminOrderDetails />
                  </Index>
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
